import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion, useReducedMotion } from 'framer-motion'
import { useApp } from '../context/AppContext'
import { useCart } from '../context/CartContext'
import ProductCardSkeleton from '../components/ProductCardSkeleton'
import DeliveryLocationPicker from '../components/DeliveryLocationPicker'
import { SITE_URL } from '../config/siteConfig'

const getEffectivePrice = (product) => {
  return product.discountPercent > 0
    ? Math.round(product.price * (1 - product.discountPercent / 100))
    : product.price
}

// Line-icon set for this page. Kept as one small component instead of an
// icon package - the homepage only needs about a dozen simple glyphs and
// this avoids a new dependency for that.
const Icon = ({ name, className = 'w-5 h-5', strokeWidth = 1.8 }) => {
  const common = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true
  }
  const paths = {
    leaf: <><path d="M20 4c-7.5.2-12.5 3-15 8.3-1.1 2.4-.7 5.5 1 7.7 2.1-6.2 6-9.9 11.7-11.7-4.3 2.2-7.1 5.1-9 9.2" /><path d="M7.7 17.5c3.9.2 7.4-1.4 9.5-4.2C19.5 10.3 20 7.1 20 4" /></>,
    bag: <><path d="M6 8h12l1 13H5L6 8z" /><path d="M9 8V6a3 3 0 016 0v2" /></>,
    play: <path d="M9 7.5v9l7.5-4.5-7.5-4.5z" fill="currentColor" stroke="none" />,
    pin: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1116 0z" /><circle cx="12" cy="10" r="2.5" /></>,
    pinFilled: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1116 0z" fill="currentColor" stroke="none" /></>,
    snow: <><path d="M12 2v20M4.2 6.5l15.6 11M19.8 6.5l-15.6 11" /><path d="M9.5 4L12 6.5 14.5 4M9.5 20l2.5-2.5 2.5 2.5M4.2 8.8l3.4-.4.4-3.4M19.8 8.8l-3.4-.4-.4-3.4M4.2 17.2l3.4.4.4 3.4M19.8 17.2l-3.4.4-.4 3.4" /></>,
    cow: <><path d="M5 10c-1.5 0-2-1.5-1-2.5S6 7 6 7M19 10c1.5 0 2-1.5 1-2.5S18 7 18 7" /><path d="M6.5 9C6 6 8.5 4 12 4s6 2 5.5 5c1 1 1.5 2.5 1 4-.3 3.5-2.8 6-6.5 6s-6.2-2.5-6.5-6c-.5-1.5 0-3 1-4z" /><circle cx="9.3" cy="12" r="0.9" fill="currentColor" stroke="none" /><circle cx="14.7" cy="12" r="0.9" fill="currentColor" stroke="none" /><path d="M10.3 15.2c.5.4 1 .6 1.7.6s1.2-.2 1.7-.6" /></>,
    chicken: <><path d="M9 21l.6-3.4M15 21l-.6-3.4" /><path d="M6.5 17.5c-2-1.4-3-3.6-2.4-6C4.7 8.6 7.4 7 8 5c.3-1 1.5-1.6 2.2-.8.4.5.3 1.2-.2 1.6 1.8-.6 3.7-.1 4.8 1.4 2.4.4 4.2 2.6 4.2 5.1 0 3.4-3.1 6.2-7 6.2s-5.5-.4-5.5-1z" /><circle cx="15.5" cy="8" r="0.8" fill="currentColor" stroke="none" /></>,
    sprout: <><path d="M12 21v-9" /><path d="M12 12c0-3.5-2.5-6-6.5-6C5 9.5 8 12 12 12z" /><path d="M12 9c0-3 2-5 5.5-5.3.6 3.5-1.7 6.3-5.5 6.3z" /></>,
    grape: <><circle cx="9" cy="9" r="2" /><circle cx="14" cy="9" r="2" /><circle cx="6.5" cy="13" r="2" /><circle cx="11.5" cy="13" r="2" /><circle cx="16.5" cy="13" r="2" /><circle cx="9" cy="17" r="2" /><circle cx="14" cy="17" r="2" /><path d="M11.5 5V3M11.5 3c1.2-.6 2-1 3.3-.7" /></>,
    scooter: <><circle cx="6.5" cy="18" r="2.2" /><circle cx="17" cy="18" r="2.2" /><path d="M6.5 18h5.5l1.5-5h3.7M12 13l-1.5-4H8" /><path d="M15.5 9h2.3" /></>,
    truck: <><path d="M3 6h11v10H3z" /><path d="M14 9h4l3 3v4h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></>,
    route: <><circle cx="6" cy="18" r="2" /><circle cx="18" cy="6" r="2" /><path d="M7.5 16.5c3-3.5 2-7.5 5-8.5 1.6-.5 2.8.3 4 1" strokeDasharray="2.5 3" /></>,
    shield: <><path d="M12 3l7 3v5c0 4.5-2.8 8.1-7 10-4.2-1.9-7-5.5-7-10V6l7-3z" /><path d="M9.5 12l1.7 1.7 3.6-4" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.3 2.3L16 10" /></>,
    arrow: <path d="M5 12h13M13 6l6 6-6 6" />
  }
  return <svg {...common}>{paths[name]}</svg>
}

// One-time, on-mount reveal for anything that isn't a Largest Contentful
// Paint candidate. The hero heading and hero image intentionally skip this -
// pre-rendering already writes them into the raw HTML fully visible, so a
// mount-triggered fade would just hide them again on hydration and delay
// mobile LCP for a visual flourish the static markup never needed.
const Reveal = ({ children, className = '', delay = 0, y = 24, once = true, mode = 'view', reduceMotion = false }) => {
  const hidden = reduceMotion ? {} : { opacity: 0, y }
  const shown = { opacity: 1, y: 0 }
  const viewportProps = mode === 'view' ? { whileInView: shown, viewport: { once, margin: '-60px' } } : { animate: shown }
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : hidden}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      {...viewportProps}
    >
      {children}
    </motion.div>
  )
}

const heroBadges = [
  { icon: 'leaf', label: 'Freshly Prepared', pos: 'top-[6%] left-[2%] lg:-left-2', float: '6.5s', delay: 0.55 },
  { icon: 'pin', label: 'Karachi Delivery', pos: 'top-[2%] right-[4%] lg:-right-2', float: '5.5s', delay: 0.68 },
  { icon: 'snow', label: 'No Frozen Stock', pos: 'top-[46%] right-[-2%] lg:-right-4', float: '7s', delay: 0.8 }
]

const categories = [
  { name: 'Beef', tagline: 'Premium cuts, 100% fresh.', image: '/beef-composition.webp', icon: 'cow', accent: '#A92C28', bg: '#FFF8F6', border: 'rgba(169,44,40,0.14)' },
  { name: 'Chicken', tagline: 'Farm fresh, tender & juicy.', image: '/chicken-composition.webp', icon: 'chicken', accent: '#D89000', bg: '#FFFAF0', border: 'rgba(216,144,0,0.16)' },
  { name: 'Vegetables', tagline: 'Handpicked daily, farm fresh.', image: '/vegetables-composition.webp', icon: 'sprout', accent: '#2E8B3C', bg: '#F2F8ED', border: 'rgba(46,139,60,0.16)' },
  { name: 'Fruits', tagline: 'Naturally sweet, full of nutrition.', image: '/fruits-composition.webp', icon: 'grape', accent: '#734493', bg: '#F8F5FB', border: 'rgba(115,68,147,0.15)' }
]

// Real zone boundaries and charges, mirrored from worker/src/distance.js and
// worker/src/pricing.js (server is the source of truth; this is display copy
// only). Bulk orders are Rs. 5,000+.
const deliveryZones = [
  { key: 'zone1', range: '0–10 km', title: 'Standard Delivery', charge: 'Rs. 150', note: 'Available for all orders', icon: 'scooter' },
  { key: 'zone2', range: '10–30 km', title: 'Extended Delivery', charge: 'Rs. 300', note: 'Bulk orders only (Rs. 5,000+)', icon: 'truck' },
  { key: 'zone3', range: '30–50 km', title: 'Far Zone Delivery', charge: 'Rs. 500', note: 'Bulk orders only (Rs. 5,000+)', icon: 'route' }
]

const trustItems = [
  { icon: 'leaf', title: 'Freshly Prepared', desc: 'Prepared after your order.' },
  { icon: 'snow', title: 'Never Frozen', desc: 'Fresh products, never frozen stock.' },
  { icon: 'shield', title: 'Hygienically Packed', desc: 'Packed with care & hygiene.' },
  { icon: 'check', title: 'Verified Delivery Distance', desc: 'Transparent, distance-based delivery.' }
]

// Divider borders for the trust strip below: 1 column on mobile (full width
// per item - a 2-column grid was too narrow for "Verified Delivery
// Distance" next to an icon and made the text spill past the card edge),
// 2 columns at sm, 4 at lg. Indexed to match trustItems.
const trustDividerClasses = [
  '',
  'border-t sm:border-t-0 sm:border-l',
  'border-t lg:border-t-0 lg:border-l',
  'border-t sm:border-l lg:border-t-0'
]

const Home = () => {
  const reduceMotion = useReducedMotion()
  const { products, productsLoaded } = useApp()
  const { addToCart } = useCart()
  const [showZoneChecker, setShowZoneChecker] = useState(false)
  const [zoneResult, setZoneResult] = useState(null)
  const [showStickyCta, setShowStickyCta] = useState(false)
  const heroCtaRef = useRef(null)

  const freshPicks = products.filter((product) => product.available).slice(0, 4)

  const handleAddToCart = (event, product) => {
    addToCart(product)
    const card = event.currentTarget.closest('.fresh-pick-card')
    const image = card?.querySelector('img')
    if (!image) return
    window.dispatchEvent(new CustomEvent('fly-to-cart', {
      detail: { imageSrc: product.image, startRect: image.getBoundingClientRect() }
    }))
  }

  // The sticky mobile "Shop Fresh" bar only appears once the hero's own
  // Shop Fresh button has scrolled out of view - mirrors the same
  // IntersectionObserver pattern ProductDetail.jsx uses for its sticky
  // Add to Cart bar, so there's never a moment with the CTA showing twice.
  useEffect(() => {
    const el = heroCtaRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { rootMargin: '-72px 0px 0px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const zoneInfo = zoneResult?.zone ? deliveryZones.find((zone) => zone.key === zoneResult.zone) : null

  return (
    <div className="bg-[#FFFEFA] text-[#162528]">
      <Helmet>
        <title>Fresh Meat, Chicken, Fruits &amp; Vegetables in Karachi | Akhtar Sabir Fresh</title>
        <meta name="description" content="Buy fresh chicken, beef, fruits and vegetables in Karachi. Cut and prepared only after you order - no old stock, no frozen storage. Same-day and next-day delivery across Karachi." />
        <link rel="canonical" href={SITE_URL} />
      </Helmet>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fffef9, #faf9f1)' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
          <Icon name="leaf" className="absolute w-6 h-6 top-[14%] left-[6%] text-[#16852B]/15 -rotate-12" />
          <Icon name="leaf" className="absolute w-5 h-5 top-[68%] left-[3%] text-[#16852B]/15 rotate-6" />
          <Icon name="leaf" className="absolute w-7 h-7 top-[8%] left-[44%] text-[#16852B]/10 rotate-[24deg]" />
          <Icon name="leaf" className="absolute w-5 h-5 top-[80%] left-[40%] text-[#16852B]/15 -rotate-6" />
        </div>

        <div className="relative max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-0 lg:min-h-[740px] flex items-center">
          <div className="grid lg:grid-cols-[43%_57%] gap-14 lg:gap-8 items-center w-full">
            <div className="relative z-10">
              {/* Always visible on mount - this is the LCP text on mobile.
                  See the Reveal component comment above for why. */}
              <h1 className="font-display leading-[1.03] text-[clamp(40px,6vw,74px)]" style={{ fontWeight: 560 }}>
                <span className="block text-[#162528]">Fresh From Market.</span>
                <span className="block text-[#16852B]">Delivered To Your Door.</span>
              </h1>

              <Reveal delay={0.1} y={16} reduceMotion={reduceMotion}>
                <p className="mt-6 text-[#66706D] text-[18px] sm:text-[19px] leading-[1.7] max-w-[600px]">
                  Premium quality meats, chicken, fruits &amp; vegetables prepared fresh and delivered quickly across <span className="text-[#16852B] font-semibold">Karachi</span>.
                </p>
              </Reveal>

              <Reveal delay={0.2} y={14} reduceMotion={reduceMotion}>
                <div ref={heroCtaRef} className="flex flex-wrap gap-4 mt-9">
                  <Link to="/shop" className="inline-flex items-center gap-2.5 h-[60px] sm:h-16 px-8 rounded-xl bg-[#16852B] hover:bg-[#1F8B35] text-white font-semibold text-[15px] sm:text-base shadow-[0_12px_35px_rgba(23,70,36,0.18)] hover:shadow-[0_18px_45px_rgba(23,70,36,0.24)] transition-all duration-300">
                    <Icon name="bag" className="w-5 h-5" />
                    Shop Fresh
                  </Link>
                  <Link to="/preorder" className="group inline-flex items-center gap-2.5 h-[60px] sm:h-16 px-8 rounded-xl bg-transparent hover:bg-[#F2F8ED] border border-[#16852B]/20 hover:border-[#16852B]/50 text-[#16852B] font-semibold text-[15px] sm:text-base transition-all duration-300">
                    <span className="w-6 h-6 rounded-full border-2 border-[#16852B] flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5">
                      <Icon name="play" className="w-3 h-3 ml-0.5" strokeWidth={0} />
                    </span>
                    How Delivery Works
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* Grocery composition + floating badges. The <img> itself stays
                always-visible for the same LCP reason as the H1 above -
                fetchPriority="high" only means something if it isn't also
                starting at opacity:0. */}
            <div className="relative z-10 min-h-[340px] sm:min-h-[420px] lg:min-h-[500px] flex items-center justify-center">
              <div aria-hidden="true" className="absolute w-[85%] h-[85%] rounded-full bg-[#16852B]/[0.06] blur-3xl" />
              <img
                src="/hero-composition.webp"
                alt="Fresh beef, chicken, fruits and vegetables from Akhtar Sabir Fresh, packed in a paper grocery bag"
                width={1700}
                height={957}
                fetchPriority="high"
                className="relative z-10 w-full max-w-[720px] h-auto object-contain drop-shadow-[18px_30px_28px_rgba(22,37,40,0.24)]"
              />

              {heroBadges.map((badge) => (
                <Reveal key={badge.label} delay={badge.delay} y={10} reduceMotion={reduceMotion} className={`absolute ${badge.pos} z-20`}>
                  <div
                    className="w-[104px] sm:w-[112px] rounded-2xl bg-white shadow-[0_16px_36px_rgba(23,70,36,0.14)] flex flex-col items-center justify-center text-center py-3.5 px-2 gap-1.5"
                    style={reduceMotion ? undefined : { animation: `float-badge ${badge.float} ease-in-out infinite` }}
                  >
                    <span className="w-8 h-8 rounded-full bg-[#F2F8ED] text-[#16852B] flex items-center justify-center">
                      <Icon name={badge.icon} className="w-4 h-4" />
                    </span>
                    <span className="text-[11.5px] font-semibold leading-tight text-[#162528]">{badge.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <div aria-hidden="true" className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-[130%] h-20 bg-[#FFFEFA]" style={{ borderRadius: '100% 100% 0 0' }} />
      </section>

      {/* ================= SHOP BY CATEGORY ================= */}
      <section className="py-[100px] lg:py-[130px]">
        <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal reduceMotion={reduceMotion} className="text-center max-w-2xl mx-auto mb-14">
            <div className="flex items-center justify-center gap-3 text-[#16852B]/50 mb-4">
              <span className="w-8 h-px bg-current" />
              <Icon name="leaf" className="w-4 h-4" />
              <span className="w-8 h-px bg-current" />
            </div>
            <h2 className="font-display text-[40px] sm:text-[48px]" style={{ fontWeight: 560 }}>
              <span className="text-[#162528]">Shop by </span>
              <span className="text-[#16852B]">Category</span>
            </h2>
            <p className="text-[#66706D] mt-3">Fresh, premium quality products delivered to your door.</p>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Reveal key={category.name} delay={index * 0.08} y={40} reduceMotion={reduceMotion}>
                <Link
                  to="/shop"
                  className="group relative flex flex-col h-[420px] sm:h-[500px] lg:h-[530px] rounded-[24px] overflow-hidden shadow-[0_12px_35px_rgba(23,70,36,0.06)] hover:shadow-[0_18px_45px_rgba(23,70,36,0.10)] hover:-translate-y-2 transition-all duration-300"
                  style={{ backgroundColor: category.bg, border: `1px solid ${category.border}` }}
                >
                  <div className="relative z-10 p-5 sm:p-6 pb-0">
                    <span className="w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center" style={{ color: category.accent }}>
                      <Icon name={category.icon} className="w-5 h-5" />
                    </span>
                    <h3 className="font-display text-2xl sm:text-3xl mt-4" style={{ color: category.accent, fontWeight: 560 }}>{category.name}</h3>
                    <p className="text-[#66706D] text-sm mt-1">{category.tagline}</p>
                    <span
                      className="mt-4 w-9 h-9 rounded-full border flex items-center justify-center transition-colors duration-300 group-hover:text-white"
                      style={{ borderColor: category.accent, color: category.accent, '--tw-bg-opacity': 1, backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = category.accent }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                    >
                      <Icon name="arrow" className="w-4 h-4" />
                    </span>
                  </div>

                  <div className="relative flex-1 mt-2 flex items-end justify-center overflow-hidden px-2">
                    <img
                      src={category.image}
                      alt={`Fresh ${category.name.toLowerCase()} for sale in Karachi - Akhtar Sabir Fresh`}
                      loading="lazy"
                      className="w-full h-full object-contain object-bottom transition-transform duration-500 group-hover:scale-[1.045]"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TODAY'S FRESH PICKS ================= */}
      {(!productsLoaded || freshPicks.length > 0) && (
        <section className="py-[100px] lg:py-[120px] bg-white">
          <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
            <Reveal reduceMotion={reduceMotion} className="flex items-end justify-between gap-5 mb-12">
              <h2 className="font-display text-[36px] sm:text-[44px] text-[#162528] flex items-center gap-3" style={{ fontWeight: 560 }}>
                Today&apos;s Fresh Picks
                <Icon name="leaf" className="w-6 h-6 text-[#16852B]/60 -rotate-12" />
              </h2>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-[#16852B] font-semibold hover:text-[#1F8B35] transition-colors">
                View All Products <Icon name="arrow" className="w-4 h-4" />
              </Link>
            </Reveal>

            {!productsLoaded ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                {Array.from({ length: 4 }).map((_, index) => <ProductCardSkeleton key={index} compact />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                {freshPicks.map((product, index) => (
                  <motion.article
                    key={product.id}
                    className="fresh-pick-card group rounded-[20px] bg-white border border-[#16852B]/[0.08] shadow-[0_12px_35px_rgba(23,70,36,0.05)] hover:shadow-[0_18px_45px_rgba(23,70,36,0.10)] hover:border-[#16852B]/25 hover:-translate-y-1.5 transition-all duration-300 overflow-hidden"
                    initial={reduceMotion ? false : { opacity: 0, y: 35 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div className="relative bg-[#FBF8F0] p-4" style={{ aspectRatio: '4 / 3' }}>
                      <img
                        src={product.image}
                        alt={`Fresh ${product.name} for sale in Karachi - Akhtar Sabir Fresh`}
                        width={420}
                        height={315}
                        loading="lazy"
                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.035]"
                      />
                      {product.discountPercent > 0 && (
                        <span className="absolute top-3 right-3 rounded-full bg-[#A92C28] text-white text-[11px] font-bold px-3 py-1.5">
                          -{product.discountPercent}%
                        </span>
                      )}
                    </div>

                    <div className="p-5 pt-4">
                      <h3 className="font-display text-[20px] sm:text-[21px] font-semibold text-[#162528]">{product.name}</h3>
                      <div className="mt-2 mb-4">
                        {product.discountPercent > 0 && <span className="text-sm line-through text-[#66706D]/60 mr-2">Rs. {product.price}</span>}
                        <span className="text-[#16852B] font-bold text-lg">Rs. {getEffectivePrice(product)}</span>
                        <span className="text-[#66706D] text-sm"> / {product.unit}</span>
                      </div>
                      <button
                        onClick={(event) => handleAddToCart(event, product)}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#16852B] text-[#16852B] group-hover:bg-[#16852B] group-hover:text-white font-semibold text-sm py-3 transition-colors duration-300"
                      >
                        <Icon name="bag" className="w-4 h-4" />
                        Add to Cart
                      </button>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}

            <div className="text-center mt-9 sm:hidden">
              <Link to="/shop" className="inline-flex items-center gap-1.5 text-[#16852B] font-semibold">View All Products <Icon name="arrow" className="w-4 h-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* ================= DELIVERY + TRUST + CTA ================= */}
      <section className="py-[100px] px-6 sm:px-10 lg:px-16">
        <div className="max-w-[1500px] mx-auto">
          <Reveal reduceMotion={reduceMotion} className="rounded-[28px] bg-[#FBF8F0] border border-[#16852B]/[0.06] p-7 sm:p-10 lg:p-12">
            <div className="grid lg:grid-cols-[35%_65%] gap-10 items-center">
              <div>
                <h2 className="font-display text-[34px] sm:text-[40px] leading-[1.1] flex items-start gap-2" style={{ fontWeight: 560 }}>
                  <span className="text-[#162528]">Delivery Across<br /><span className="text-[#16852B]">Karachi</span></span>
                  <Icon name="leaf" className="w-5 h-5 text-[#16852B]/50 mt-2 shrink-0" />
                </h2>
                <p className="text-[#66706D] mt-4 leading-[1.7]">Fast &amp; reliable delivery to your doorstep. Choose your zone to see delivery details.</p>
                <button
                  type="button"
                  onClick={() => setShowZoneChecker((visible) => !visible)}
                  className="inline-flex items-center gap-2 mt-7 h-12 px-6 rounded-xl bg-white border border-[#16852B]/25 hover:border-[#16852B]/50 text-[#16852B] font-semibold text-sm transition-colors"
                >
                  <Icon name="pin" className="w-4 h-4" />
                  {showZoneChecker ? 'Hide Map' : 'Check Your Area'}
                </button>
                <Link to="/delivery" className="block mt-3 text-xs text-[#66706D] hover:text-[#16852B] transition-colors">
                  View full delivery info →
                </Link>
              </div>

              {showZoneChecker ? (
                <div className="rounded-2xl overflow-hidden bg-[#F2F8ED] p-4 [&_.leaflet-container]:rounded-xl">
                  <DeliveryLocationPicker onLocationChange={setZoneResult} />
                  {zoneInfo && zoneResult?.status === 'ready' && (
                    <p className="text-sm text-[#162528] mt-3 bg-white rounded-xl px-4 py-3">
                      You&apos;re in <strong style={{ color: '#16852B' }}>{zoneInfo.title}</strong> ({zoneInfo.range}) &mdash; delivery is <strong>{zoneInfo.charge}</strong>. {zoneInfo.note}.
                    </p>
                  )}
                  {zoneResult && zoneResult.zone === null && zoneResult.status === 'ready' && (
                    <p className="text-sm text-[#A92C28] mt-3 bg-white rounded-xl px-4 py-3">
                      That location is outside our delivery range (50 km by road).
                    </p>
                  )}
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden bg-[#F2F8ED] h-[220px] sm:h-[280px]">
                  <svg viewBox="0 0 600 280" className="absolute inset-0 w-full h-full" aria-hidden="true">
                    <path d="M0 220 Q150 250 300 225 T600 235 V280 H0 Z" fill="#DCEEE0" opacity="0.7" />
                    <path d="M60 40 Q220 10 340 70 T560 60" stroke="#BFE0C8" strokeWidth="3" fill="none" strokeDasharray="1 10" strokeLinecap="round" />
                    <path d="M30 140 Q200 110 380 150 T580 170" stroke="#BFE0C8" strokeWidth="3" fill="none" strokeDasharray="1 10" strokeLinecap="round" />
                    <path d="M90 30 Q120 130 90 230" stroke="#BFE0C8" strokeWidth="3" fill="none" strokeDasharray="1 10" strokeLinecap="round" />
                    <path d="M480 20 Q450 130 500 240" stroke="#BFE0C8" strokeWidth="3" fill="none" strokeDasharray="1 10" strokeLinecap="round" />
                    <circle cx="150" cy="90" r="34" fill="#DCEEE0" opacity="0.8" />
                    <circle cx="450" cy="110" r="44" fill="#DCEEE0" opacity="0.6" />
                    <circle cx="300" cy="200" r="30" fill="#DCEEE0" opacity="0.7" />
                    <rect x="130" y="72" width="16" height="16" rx="2" fill="#BFE0C8" transform="rotate(45 138 80)" />
                    <rect x="430" y="95" width="14" height="14" rx="2" fill="#BFE0C8" transform="rotate(45 437 102)" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Icon name="pinFilled" className="w-9 h-9 text-[#16852B] drop-shadow-[0_4px_8px_rgba(23,70,36,0.25)]" />
                    <span className="mt-1.5 font-display text-lg text-[#16852B]" style={{ fontWeight: 560 }}>Karachi</span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {deliveryZones.map((zone, index) => (
                <Reveal key={zone.title} delay={index * 0.08} y={20} reduceMotion={reduceMotion}>
                  <div className="relative h-full rounded-2xl bg-white border border-[#16852B]/[0.07] p-5 overflow-hidden">
                    <span className="inline-block text-[11px] font-bold text-white bg-[#16852B] rounded-full px-3 py-1">{zone.range}</span>
                    <h3 className="font-display text-lg text-[#162528] mt-3" style={{ fontWeight: 560 }}>{zone.title}</h3>
                    <div className="flex items-center justify-between mt-3">
                      <div>
                        <p className="text-[#66706D] text-xs">Delivery Charge</p>
                        <p className="text-[#16852B] font-bold">{zone.charge}</p>
                      </div>
                    </div>
                    <p className="text-[#66706D] text-xs mt-2">{zone.note}</p>
                    <span className="absolute -right-3 -bottom-3 w-16 h-16 rounded-full bg-[#F2F8ED] text-[#16852B] flex items-center justify-center">
                      <Icon name={zone.icon} className="w-6 h-6" />
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-[#16852B]/[0.07] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {trustItems.map((item, index) => (
                <div key={item.title} className={`flex items-center gap-3 p-5 border-[#16852B]/[0.07] ${trustDividerClasses[index]}`}>
                  <span className="w-10 h-10 rounded-xl bg-[#F2F8ED] text-[#16852B] flex items-center justify-center shrink-0">
                    <Icon name={item.icon} className="w-5 h-5" />
                  </span>
                  <span className="min-w-0">
                    <strong className="block text-sm font-bold text-[#162528] leading-tight">{item.title}</strong>
                    <span className="block text-xs text-[#66706D] mt-0.5 leading-tight">{item.desc}</span>
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal reduceMotion={reduceMotion} className="relative mt-8 rounded-[26px] bg-[#F2F8ED] overflow-hidden">
            <div className="relative flex flex-col lg:flex-row items-center gap-6 lg:gap-10 px-6 sm:px-10 py-9 lg:py-0 lg:h-[170px]">
              <img
                src="/hero-composition-sm.webp"
                alt=""
                aria-hidden="true"
                width={640}
                height={360}
                loading="lazy"
                className="w-[180px] sm:w-[220px] h-auto object-contain shrink-0 -mb-4 lg:mb-0"
              />
              <div className="flex-1 text-center lg:text-left">
                <p className="font-display text-xl sm:text-2xl text-[#162528] leading-snug" style={{ fontWeight: 560 }}>
                  Fresh groceries are only <span className="text-[#16852B]">a few clicks away.</span>
                </p>
                <p className="text-[#66706D] text-sm mt-1.5">Shop premium quality meat, chicken, fruits &amp; vegetables. Fast delivery. Always fresh.</p>
              </div>
              <Link to="/shop" className="inline-flex items-center gap-2.5 h-14 px-7 rounded-xl bg-[#16852B] hover:bg-[#1F8B35] text-white font-semibold shrink-0 shadow-[0_12px_30px_rgba(23,70,36,0.2)] transition-colors">
                <Icon name="bag" className="w-4 h-4" />
                Shop Fresh Now
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {showStickyCta && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.3 }}
          className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#16852B]/10 shadow-[0_-4px_16px_rgba(23,70,36,0.08)] px-4 py-3"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <div className="flex items-center gap-3">
            <span className="flex-1 min-w-0">
              <strong className="block text-sm font-bold text-[#162528] truncate">Fresh From Market.</strong>
              <span className="block text-xs text-[#66706D] truncate">Delivered across Karachi.</span>
            </span>
            <Link to="/shop" className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#16852B] text-white font-semibold text-sm shrink-0">
              <Icon name="bag" className="w-4 h-4" />
              Shop Fresh
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default Home
